import { matchPath, Outlet, useLocation } from "react-router-dom";
import { useAppState } from "../../state/App";
import { Disclaimer, useDisclaimer } from "../../components/common/Disclaimer";
import { UserEditor } from "@myplant-io/user-management";
import { IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import SchoolIcon from "@mui/icons-material/School";
import HistoryIcon from "@mui/icons-material/History";
import StorageIcon from "@mui/icons-material/Storage";
import AddIcon from "@mui/icons-material/Add";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LocalGroceryStoreIcon from "@mui/icons-material/LocalGroceryStore";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import { MyAppsLauncher } from "@myplant-io/app-launcher";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  useAuth,
  useCookieConsent,
  useDialog,
  useUsageTracking,
} from "@myplant-io/hooks";
import { defineMessages } from "react-intl";
import { RouteNames } from "../../RouteNames";
import { GetHelpDialog } from "../../components/GetHelpDialog";
import { useIsKiosk } from "../../hooks/useIsKiosk";
import { useEnsureMode } from "../../hooks/useModes";
import { GetManual } from "../../components/GetManual";
import {
  API_BASE_URL,
  CLIENT_ID,
  MYPLANT_URL,
  SUPPORTED_LANGUAGES,
  WS_BASE_URL,
} from "../../constants";
import {
  AppBar,
  AppType,
  CookieConsentLink,
  Drawer,
  DrawerFooterContentBox,
  ListItemType,
} from "@myplant-io/app-shell";

const messages = defineMessages({
  chat: {
    id: "chat",
    defaultMessage: "Chat",
  },
  knowledgeBase: {
    id: "knowledgeBase",
    defaultMessage: "Knowledge Base",
  },
  memory: {
    id: "memory",
    defaultMessage: "Memory",
  },
  modeStore: {
    id: "modeStore",
    defaultMessage: "Agent Store",
  },
  logout: {
    id: "logout",
    defaultMessage: "Logout",
  },
  endImpersonation: {
    id: "common.action.endImpersonation",
    defaultMessage: "End impersonation",
  },
});

function getDrawerItems(
  impersonated: boolean,
  handleAction: (path: string) => void
): ListItemType[] {
  return [
    {
      id: "chat",
      label: messages.chat,
      Icon: ChatIcon,
      path: /chat/,
      onAction: () => handleAction(RouteNames.CHAT),
      permission: "",
    },
    {
      id: "knowledgeBase",
      label: messages.knowledgeBase,
      Icon: SchoolIcon,
      path: /knowledge-base/,
      onAction: () => handleAction(RouteNames.KNOWLEDGE_BASE),
      permission: "",
    },
    {
      id: "memory",
      label: messages.memory,
      Icon: StorageIcon,
      path: /memory/,
      onAction: () => handleAction(RouteNames.MEMORY),
      permission: "",
    },
    {
      id: "mode-store",
      label: messages.modeStore,
      Icon: LocalGroceryStoreIcon,
      path: /mode-store/,
      onAction: () => handleAction(RouteNames.MODE_STORE),
      permission: "",
    },
    {
      id: "Logout",
      label: impersonated ? messages.endImpersonation : messages.logout,
      Icon: ExitToAppIcon,
      path: /logout/,
      onAction: () => handleAction(RouteNames.LOGOUT),
      permission: "",
    },
  ];
}

const Layout = () => {
  const isKiosk = useIsKiosk();
  const { hasCookieConsent } = useCookieConsent();
  const { accepted } = useDisclaimer();
  const theme = useTheme();
  const {
    state: { messages, isChatHistoryOpen },
    actions: { setChatHistory, setCurrentConversationId, setMessages },
  } = useAppState();
  useEnsureMode();
  const is2lg = useMediaQuery(theme.breakpoints.up("2lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);

  const editUserModal = useDialog();
  const { data: authData, isFetched: isAuthDataFetched } =
    useAuth(API_BASE_URL);
  const user = authData?.user;
  const location = useLocation();
  const { trackEvent, trackPageView, enableLinkTracking } = useMatomo();

  enableLinkTracking();

  const { mutate: track } = useUsageTracking({ wsBaseUrl: "/ws" });

  const navigate = useNavigate();

  useEffect(() => {
    track({
      appId: CLIENT_ID,
      eventType: "login",
    });
  }, [track]);

  useEffect(() => {
    // track page view on each location change
    if (location.pathname) {
      trackPageView({
        documentTitle: document.title,
      });
    }
  }, [location, trackPageView]);

  const newChat = () => {
    setCurrentConversationId(undefined);
    setMessages([]);
  };

  const actions = (
    <div className="flex flex-row">
      {messages.length > 0 && !is2lg && (
        <Tooltip key="new" title="Start new conversation">
          <IconButton
            size="large"
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
              newChat();
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      )}
      {location.pathname.includes("chat") && (
        <Tooltip
          key="history"
          title={isChatHistoryOpen ? "Hide chat history" : "Show chat history"}
        >
          <IconButton
            size="large"
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
              setChatHistory(!isChatHistoryOpen);
            }}
          >
            <HistoryIcon />
          </IconButton>
        </Tooltip>
      )}
      {!isMobile && <GetManual />}
      {!isMobile && <GetHelpDialog />}
      {!isMobile && (
        <MyAppsLauncher
          edge="end"
          wsBaseUrl="/ws"
          onNavigate={navigate}
          onTrackEvent={trackEvent}
        />
      )}
    </div>
  );

  if (isKiosk) {
    return (
      <div className="h-full flex flex-col">
        <Outlet />;
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <AppBar
        title="myAI Assistant"
        actions={actions}
        onDrawerOpen={() => setOpen(true)}
      />
      <Drawer
        apiBaseUrl={API_BASE_URL}
        wsBaseUrl={WS_BASE_URL}
        open={open}
        handleClose={() => setOpen(false)}
        app={AppType.AI}
        pathname={location.pathname}
        isActive={(pathname, pattern) =>
          typeof pattern === "string"
            ? matchPath({ path: pattern }, pathname) !== null
            : pattern.test(pathname)
        }
        additionalUserInfo={"User"}
        drawerItems={getDrawerItems(!!authData?.impersonated, navigate)}
        additionalFooterContent={
          hasCookieConsent ? (
            <DrawerFooterContentBox>
              <CookieConsentLink />
            </DrawerFooterContentBox>
          ) : null
        }
        userSettings={{
          myPlantUrl: MYPLANT_URL,
          supportedLanguages: SUPPORTED_LANGUAGES,
        }}
      />
      <Outlet />

      {isAuthDataFetched && user && !accepted && <Disclaimer />}

      <UserEditor
        wsBaseUrl={WS_BASE_URL}
        apiBaseUrl={API_BASE_URL}
        open={editUserModal.open}
        username={user?.name ?? ""}
        warnAboutSubscription
        onClose={() => editUserModal.onClose()}
      />
    </div>
  );
};

export default Layout;
